import React, { useEffect, useState } from "react";
import { t } from "ttag";

import type { HeaderContent } from ".";
import { HeaderContext } from "./HeaderContext";
import { MainNav } from "./MainNav";
import { MobileMenu } from "./MobileMenu";
import { MobileNav } from "./MobileNav";
import { SkipLink } from "./SkipLink";
import { TopNav } from "./TopNav";
import { Underlay } from "./Underlay";
import { useIsAtTopOfPage } from "./hooks/useIsAtTopOfPage";
import { useScrollDirection } from "./hooks/useScrollDirection";

import styles from "./Header.module.scss";

/** Data attribute on the top-level nav. Consumers (e.g. Loading overlay) target it for inert-exclusion. */
export const MAIN_NAV_DATA_ATTR = "data-main-nav";

interface Props {
    headerContent: HeaderContent;
    favoritesUrl: string | null;
    searchUrl: string;
    /** Whether the header renders transparently over the first body block */
    overlapNav?: boolean;
}

export const Header = ({
    headerContent,
    favoritesUrl,
    searchUrl,
    overlapNav = false,
}: Props) => {
    const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState<boolean | null>(null);

    const { scrollDirection } = useScrollDirection();
    const isAtTop = useIsAtTopOfPage();

    // Reset mobile state when switching to desktop layout
    useEffect(() => {
        if (isMobileLayout === false) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- closes the mobile menu when the layout switches to desktop
            setMobileMenuOpen(false);
        }
    }, [isMobileLayout]);

    // Close mobile menu when desktop submenu opens
    useEffect(() => {
        if (activeSubmenu && mobileMenuOpen) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- closes the mobile menu when a desktop submenu opens
            setMobileMenuOpen(false);
        }
    }, [activeSubmenu, mobileMenuOpen]);

    // Escape closes any open submenu, unless focus is in a search input, because
    // the user might be pressing escape in there to close an autocomplete menu
    // or clear the input, and it'd be super annoying to try and work around those
    useEffect(() => {
        if (!activeSubmenu) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (
                event.key === "Escape" &&
                !document.activeElement?.matches("input[type='search']")
            ) {
                setActiveSubmenu(null);
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [activeSubmenu]);

    const contextValue = {
        headerContent,
        favoritesUrl,
        searchUrl,
        activeSubmenu,
        setActiveSubmenu,
        mobileMenuOpen,
        setMobileMenuOpen,
        isMobileLayout,
        setIsMobileLayout,
        isOverlapping: overlapNav,
        isAtTop,
        scrollDirection,
    };

    return (
        <HeaderContext.Provider value={contextValue}>
            <SkipLink />
            <nav
                className={styles.nav}
                aria-label={t`Main Navigation`}
                {...{ [MAIN_NAV_DATA_ATTR]: "" }}
            >
                <TopNav />
                <MainNav />
                <MobileNav />
                <MobileMenu />
                <Underlay />
            </nav>
        </HeaderContext.Provider>
    );
};
